import { interpolateClosedSpline, type Point2D } from "./spline";

export interface Track {
  // Starting position
  startX: number;
  startY: number;
  startAngle: number;

  // Centerline points for rendering and collision (world coords, closed loop)
  centerline: { x: number; y: number }[];

  // Track half-width
  halfWidth: number;

  // Start/finish line position (index into centerline)
  startFinishIndex: number;
}

/**
 * Creates the Grand Prix circuit — a ~1.8km track with 8 corners.
 *
 * Layout (top-down, +x right, +y up, driven clockwise):
 *
 *                       T1 (fast right)
 *   Start/Finish ----→ ╮
 *                       │
 *                       ╰─── T2 (tight right)
 *                             │
 *        T4 (fast left) ─── T3 (hairpin left)
 *        │
 *        ╰── T5 (right kink)
 *             │
 *   T7 ──── T6 (chicane)
 *   │
 *   ╰──── back to Start/Finish
 *
 * The car starts on the main straight heading +x (angle = 0).
 */
export function createGPTrack(): Track {
  const halfWidth = 10;

  // Control points defining the circuit shape (closed loop, clockwise)
  // Each point is a waypoint the spline passes through
  const controlPoints: Point2D[] = [
    // Main straight (heading +x)
    { x: -60, y: 80 },
    { x: 80, y: 80 },
    // T1: fast right sweeper
    { x: 180, y: 80 },
    { x: 230, y: 50 },
    // T2: medium right
    { x: 240, y: -10 },
    { x: 210, y: -50 },
    // T3: left hairpin
    { x: 160, y: -80 },
    { x: 100, y: -70 },
    // Short straight + T4: fast left
    { x: 50, y: -40 },
    { x: 10, y: 0 },
    // T5: right kink
    { x: -20, y: 30 },
    // T6-T7: sweeping left back to straight
    { x: -60, y: 50 },
    { x: -80, y: 70 },
  ];

  const centerline = interpolateClosedSpline(controlPoints, 30);

  // Start position: middle of main straight, on the centerline
  // The straight runs from control point 0 to 1, so index ~15 (halfway through segment 0)
  const startFinishIndex = 15;
  const startPt = centerline[startFinishIndex];

  // Start angle: direction along the centerline at start point
  const nextPt = centerline[startFinishIndex + 1];
  const startAngle = Math.atan2(
    nextPt.y - startPt.y,
    nextPt.x - startPt.x
  );

  return {
    startX: startPt.x,
    startY: startPt.y,
    startAngle,
    centerline,
    halfWidth,
    startFinishIndex,
  };
}

/**
 * Creates a simple oval track for initial physics testing.
 */
export function createOvalTrack(): Track {
  const straightLen = 200;
  const curveRadius = 60;
  const halfWidth = 12;
  const halfStraight = straightLen / 2;

  const centerline: { x: number; y: number }[] = [];
  const steps = 200;

  for (let i = 0; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    centerline.push({
      x: -halfStraight + t * straightLen,
      y: curveRadius,
    });
  }

  for (let i = 1; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    const a = Math.PI / 2 - t * Math.PI;
    centerline.push({
      x: halfStraight + Math.cos(a) * curveRadius,
      y: Math.sin(a) * curveRadius,
    });
  }

  for (let i = 1; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    centerline.push({
      x: halfStraight - t * straightLen,
      y: -curveRadius,
    });
  }

  for (let i = 1; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    const a = -Math.PI / 2 - t * Math.PI;
    centerline.push({
      x: -halfStraight + Math.cos(a) * curveRadius,
      y: Math.sin(a) * curveRadius,
    });
  }

  return {
    startX: 0,
    startY: curveRadius,
    startAngle: 0,
    centerline,
    halfWidth,
    startFinishIndex: 0,
  };
}
